import { useApplicationForm } from "@/context/ApplicationFormContext";
import {
  RECIPIENT_TYPE_LABELS,
  EQUIPMENT_LABELS,
  OBLIGATION_ITEMS,
  DOCUMENT_LABELS,
  getRequiredDocuments,
  getOptionalDocuments,
  calculateAssistance,
  formatMoney,
  BOILER_ROOM_LABELS,
} from "@/lib/application";
import { cn } from "@/lib/utils";

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  );
}

interface SectionProps {
  title: string;
  step: number;
  onEdit: () => void;
  children: React.ReactNode;
}

function Section({ title, step, onEdit, children }: SectionProps) {
  return (
    <div className="bg-white rounded-2xl border-[3px] border-border/50 overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-border/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <span className="text-[13px] font-semibold text-primary">{step}</span>
          </div>
          <h3 className="text-[14px] font-semibold">{title}</h3>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-primary hover:bg-primary/5 transition-colors"
        >
          <EditIcon className="w-4 h-4" />
          Редагувати
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

interface DataRowProps {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}

function DataRow({ label, value, mono }: DataRowProps) {
  return (
    <div className="flex justify-between py-2 border-b border-border/20 last:border-0">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className={cn("text-[12px] font-medium text-right max-w-[60%]", mono && "font-mono")}>
        {value || "—"}
      </span>
    </div>
  );
}

interface ConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}

function ConsentCheckbox({ checked, onChange, children }: ConsentCheckboxProps) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={cn(
          "w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all",
          checked
            ? "bg-primary border-primary"
            : "border-muted-foreground/30 group-hover:border-primary/50"
        )}
      >
        {checked && <CheckIcon className="w-4 h-4 text-primary-foreground" />}
      </button>
      <span className="text-[12px] text-foreground/80 leading-relaxed">
        {children}
      </span>
    </label>
  );
}

interface StepReviewProps {
  onGoToStep: (step: number) => void;
}

export function StepReview({ onGoToStep }: StepReviewProps) {
  const { formData, updateObligations } = useApplicationForm();
  const { recipient, building, equipment, documents, obligations } = formData;

  const assistance = building.floors_count
    ? calculateAssistance(building.floors_count, building.boiler_room_type)
    : null;

  const allObligationsAccepted = obligations.obligations_accepted;

  const allConsentsAccepted =
    obligations.consent_data_processing &&
    obligations.consent_terms;

  return (
    <div className="space-y-6">
      {/* Header - ЗАЯВА */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl border-[3px] border-primary/20 p-6 text-center">
        <h2 className="text-[16px] font-bold text-primary uppercase tracking-wide">
          ЗАЯВА
        </h2>
        <p className="text-[12px] text-primary/80 mt-2 max-w-lg mx-auto leading-relaxed">
          на отримання допомоги на придбання незалежних джерел електричної енергії 
          для забезпечення потреб співвласників багатоквартирних будинків
        </p>
        <div className="mt-4 text-[11px] text-muted-foreground">
          <p><span className="font-medium">Кому:</span> Міністерство розвитку громад та територій України</p>
          <p className="mt-1"><span className="font-medium">Від:</span> {recipient.organization_name || "_______________"}</p>
        </div>
      </div>

      <div>
        <h3 className="text-[15px] font-semibold mb-2">Перевірка даних</h3>
        <p className="text-[12px] text-muted-foreground mb-4">
          Перевірте правильність введених даних перед підписанням заяви
        </p>
      </div>

      {/* Section 1: Recipient */}
      <Section title="Інформація про отримувача" step={1} onEdit={() => onGoToStep(1)}>
        <div className="space-y-0">
          <DataRow
            label="Форма управління"
            value={recipient.recipient_type ? RECIPIENT_TYPE_LABELS[recipient.recipient_type] : null}
          />
          <DataRow label="Назва організації" value={recipient.organization_name} />
          <DataRow label="Код ЄДРПОУ" value={recipient.edrpou_code} mono />
          <DataRow label="Керівник" value={recipient.head_full_name} />
          <DataRow label="Юридична адреса" value={recipient.legal_address} />
          <DataRow label="Фактична адреса" value={recipient.actual_address} />
          <DataRow label="Телефон" value={recipient.phone} mono />
          <DataRow label="Email" value={recipient.email} />
          <DataRow label="IBAN" value={recipient.iban} mono />
        </div>
      </Section>

      {/* Section 2: Building */}
      <Section title="Інформація про будинок" step={2} onEdit={() => onGoToStep(2)}>
        <div className="space-y-0">
          <DataRow label="Адреса будинку" value={building.building_address} />
          <DataRow label="Кількість поверхів" value={building.floors_count} />
          <DataRow label="Наявність котельні" value={building.boiler_room_type ? BOILER_ROOM_LABELS[building.boiler_room_type] : "Немає"} />
          <DataRow label="Кількість співвласників" value={building.co_owners_count} />
          <DataRow label="Кількість квартир" value={building.apartments_count} />
          <DataRow label="Загальна площа" value={building.total_area ? `${building.total_area} м²` : null} />
        </div>

        {assistance && (
          <div className="mt-4 p-3 bg-green-50 rounded-xl">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-[11px] text-green-600">Категорія допомоги</p>
                <p className="text-[13px] font-semibold text-green-700">
                  {assistance.category === "category_a" ? "A" : assistance.category === "category_b" ? "B" : "C"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-green-600">Сума допомоги</p>
                <p className="text-[16px] font-bold text-green-700">{formatMoney(assistance.amount)}</p>
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* Section 3: Equipment */}
      <Section title="Обладнання" step={3} onEdit={() => onGoToStep(3)}>
        <div className="space-y-3">
          <div>
            <p className="text-[11px] text-muted-foreground mb-2">Обране обладнання:</p>
            <div className="flex flex-wrap gap-2">
              {equipment.equipment_types.map((type) => (
                <span
                  key={type}
                  className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-[12px] font-medium"
                >
                  {EQUIPMENT_LABELS[type]}
                </span>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-border/30">
            <p className="text-[11px] text-muted-foreground mb-2">Договір поставки:</p>
            {equipment.has_supply_contract ? (
              <>
                <DataRow label="Номер договору" value={equipment.contract_number} />
                <DataRow label="Дата договору" value={equipment.contract_date} />
                <DataRow label="Постачальник" value={equipment.supplier_name} />
                <DataRow label="ЄДРПОУ постачальника" value={equipment.supplier_edrpou} mono />
                <DataRow
                  label="Вартість"
                  value={equipment.equipment_cost ? formatMoney(equipment.equipment_cost) : null}
                />
                <DataRow label="Строк поставки" value={equipment.delivery_deadline} />
              </>
            ) : (
              <p className="text-[13px] text-muted-foreground italic">
                На даний момент договір поставки не укладено
              </p>
            )}
          </div>
        </div>
      </Section>

      {/* Section 4: Documents */}
      <Section title="Документи" step={4} onEdit={() => onGoToStep(4)}>
        {(() => {
          const requiredDocs = recipient.recipient_type
            ? getRequiredDocuments(recipient.recipient_type, building.boiler_room_type, building.has_tech_passport)
            : [];
          const optionalDocs = getOptionalDocuments();
          
          // Filter optional docs to only show uploaded ones
          const uploadedOptionalDocs = optionalDocs.filter(docType => {
            const doc = documents.find(d => d.type === docType);
            return doc?.uploaded;
          });

          return (
            <div className="space-y-2">
              {/* Required documents - always show */}
              {requiredDocs.map((docType) => {
                const doc = documents.find(d => d.type === docType);
                const isUploaded = doc?.uploaded;
                return (
                  <div
                    key={docType}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded-lg",
                      isUploaded ? "bg-green-50" : "bg-red-50"
                    )}
                  >
                    <div
                      className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center",
                        isUploaded ? "bg-green-500" : "bg-red-400"
                      )}
                    >
                      {isUploaded ? (
                        <CheckIcon className="w-4 h-4 text-white" />
                      ) : (
                        <span className="text-white text-[10px]">!</span>
                      )}
                    </div>
                    <span className={cn("text-[12px]", isUploaded ? "text-green-700" : "text-red-700")}>
                      {isUploaded ? (doc?.name || DOCUMENT_LABELS[docType]) : "Не завантажено"}
                    </span>
                  </div>
                );
              })}
              
              {/* Optional documents - only show if uploaded */}
              {uploadedOptionalDocs.map((docType) => {
                const doc = documents.find(d => d.type === docType);
                return (
                  <div
                    key={docType}
                    className="flex items-center gap-3 p-2 rounded-lg bg-blue-50"
                  >
                    <div className="w-6 h-6 rounded-full flex items-center justify-center bg-blue-500">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[12px] text-blue-700">
                      {doc?.name || DOCUMENT_LABELS[docType]}
                      <span className="text-blue-500 ml-1">(додатковий)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Section>

      {/* Obligations */}
      <div className="bg-amber-50 rounded-2xl border-[3px] border-amber-200 p-5">
        <h3 className="text-[15px] font-semibold text-amber-900 mb-4">Зобов'язання заявника</h3>
        
        <label className="flex items-start gap-3 cursor-pointer group">
          <button
            type="button"
            onClick={() => updateObligations({ obligations_accepted: !obligations.obligations_accepted })}
            className={cn(
              "w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all",
              obligations.obligations_accepted
                ? "bg-primary border-primary"
                : "border-muted-foreground/30 group-hover:border-primary/50"
            )}
          >
            {obligations.obligations_accepted && <CheckIcon className="w-4 h-4 text-primary-foreground" />}
          </button>
          <div className="flex-1">
            <p className="text-[12px] text-amber-700 mb-3">
              Підписанням заяви заявник підтверджує наступні зобов'язання:
            </p>
            <ul className="space-y-2 text-[12px] text-foreground/80 leading-relaxed">
              {OBLIGATION_ITEMS.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>{item};</span>
                </li>
              ))}
            </ul>
          </div>
        </label>

        {!allObligationsAccepted && (
          <p className="text-[11px] text-amber-600 mt-4">
            * Для подання заяви необхідно прийняти зобов'язання
          </p>
        )}
      </div>

      {/* Consents - NEW */}
      <div className="bg-slate-50 rounded-2xl border-[3px] border-slate-200 p-5">
        <h3 className="text-[15px] font-semibold text-slate-900 mb-4">Згоди заявника</h3>

        <div className="space-y-4">
          <ConsentCheckbox
            checked={obligations.consent_data_processing || false}
            onChange={(checked) => updateObligations({ consent_data_processing: checked })}
          >
            Надаю згоду на обробку моїх персональних даних відповідно до Закону України 
            «Про захист персональних даних» та Політики конфіденційності з метою реалізації 
            експериментального проєкту щодо надання державної допомоги на придбання незалежних 
            джерел електричної енергії.
          </ConsentCheckbox>

          <ConsentCheckbox
            checked={obligations.consent_terms || false}
            onChange={(checked) => updateObligations({ consent_terms: checked })}
          >
            Підтверджую, що ознайомлений(-а) з умовами реалізації експериментального проєкту 
            та Порядком надання державної допомоги і зобов'язуюсь їх дотримуватися.
          </ConsentCheckbox>
        </div>

        <button
          type="button"
          onClick={() => {
            if (allConsentsAccepted) {
              updateObligations({
                consent_data_processing: false,
                consent_terms: false,
              });
            } else {
              updateObligations({
                consent_data_processing: true,
                consent_terms: true,
              });
            }
          }}
          className={cn(
            "mt-4 px-4 py-2 rounded-lg text-[12px] font-medium transition-all",
            allConsentsAccepted
              ? "bg-slate-300 text-slate-600 hover:bg-slate-400"
              : "bg-slate-700 text-white hover:bg-slate-800"
          )}
        >
          {allConsentsAccepted ? "Скасувати всі згоди" : "Надати всі згоди"}
        </button>

        {!allConsentsAccepted && (
          <p className="text-[11px] text-slate-500 mt-3">
            * Для подання заяви необхідно надати всі згоди
          </p>
        )}
      </div>

      {/* Signature info */}
      <div className="bg-blue-50 rounded-2xl border-[3px] border-blue-200 p-5">
        <h3 className="text-[15px] font-semibold text-blue-900 mb-2">Підписання заяви</h3>
        <p className="text-[12px] text-blue-700">
          Після натискання кнопки «Підписати та подати» вам буде запропоновано підписати заяву
          за допомогою КЕП (кваліфікованого електронного підпису).
        </p>
      </div>
    </div>
  );
}
